import { PredictiveSearchComponent } from '@theme/predictive-search';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Below this width the panel becomes a full-screen overlay. Matches the CSS. */
const OVERLAY_MEDIA_QUERY = '(max-width: 1023px)';

/**
 * The header search field.
 *
 * Everything about searching — debounced fetches, result navigation, reset —
 * comes from `PredictiveSearchComponent`. This subclass only owns when the
 * panel is visible: an anchored dropdown from 1024px up, a full-screen overlay
 * below it.
 *
 * @extends PredictiveSearchComponent
 */
/**
 * Long enough to outlast `resetSearch`'s 100ms debounce and the focus it puts
 * back on the field.
 */
const REOPEN_GUARD_MS = 300;

class SearchInlineComponent extends PredictiveSearchComponent {
  #controller = new AbortController();

  #suppressOpen = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.refs.searchInput.addEventListener('focus', this.open, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    unlockScroll(this);
  }

  get #isOverlay() {
    return window.matchMedia(OVERLAY_MEDIA_QUERY).matches;
  }

  open = () => {
    if (this.#suppressOpen || this.hasAttribute('open')) return;

    this.setAttribute('open', '');
    this.refs.searchInput.setAttribute('aria-expanded', 'true');

    if (!this.#isOverlay) return;

    // `#header-component` sets `contain: layout`, which makes it the containing
    // block for fixed positioning — the overlay would be trapped inside the
    // header row. The top layer is the only way out that leaves the sticky
    // header's own containment intact.
    if (typeof this.showPopover === 'function') {
      this.setAttribute('popover', 'manual');
      this.showPopover();
      // Entering the top layer can drop focus; the field must stay live.
      this.refs.searchInput.focus();
    }

    // The overlay covers the page, so the page behind it must not scroll.
    lockScroll(this);
  };

  close = () => {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.refs.searchInput.blur();

    if (this.hasAttribute('popover')) {
      this.hidePopover();
      this.removeAttribute('popover');
    }

    unlockScroll(this);

    // Same as the search modal on close: a dismissed panel should not leave a
    // stale query and a floating Clear button sitting in the header. Guarded so
    // dismissing an untouched field costs no request.
    if (!this.refs.searchInput.value) return;

    // `resetSearch` resets the result cursor, and that setter focuses the field
    // — which lands on the `focus` listener below and reopens what was just
    // closed. Ignore openings until the reset has run its course.
    this.#suppressOpen = true;
    this.resetSearch(false);
    setTimeout(() => {
      this.#suppressOpen = false;
    }, REOPEN_GUARD_MS);
  };

  /**
   * Escape closes the panel. The base component also listens for Escape via
   * `on:keydown` and clears the field — both are wanted, so this doesn't
   * preventDefault or stop propagation.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  /**
   * A press anywhere outside the component dismisses it. `pointerdown` rather
   * than `click` so the panel closes before a link inside the page swallows
   * the interaction.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown = (event) => {
    if (!this.hasAttribute('open')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.close();
  };
}

if (!customElements.get('search-inline-component')) {
  customElements.define('search-inline-component', SearchInlineComponent);
}
